import { wxLogin } from '@/apis/wxLogin';
import { debugWechatJsapi, getWechatDebugError } from './wechatDebug';

const WECHAT_APP_ID = 'wxcbe6caecec18ba48';
const PENDING_PAY_KEY = 'cashier_wechat_pending_pay';
const AUTHORIZATION_MAX_AGE = 10 * 60 * 1000;
const JSAPI_PAY_CALLBACK_TIMEOUT = 60 * 1000;

interface IWechatAuthorizationContext {
  orderNo: string;
  appKey: string;
  appId?: string;
}

interface IPendingWechatPay extends IWechatAuthorizationContext {
  state: string;
  createdAt: number;
}

interface IWechatJsapiPayParams {
  appId: string;
  timeStamp: string;
  nonceStr: string;
  package: string;
  signType: string;
  paySign: string;
}

interface IWeixinJsBridgeResult {
  err_msg?: string;
  [key: string]: unknown;
}

type WechatJsapiPayResult =
  | { status: 'success'; rawResult: IWeixinJsBridgeResult }
  | { status: 'cancel'; message: string; rawResult: IWeixinJsBridgeResult }
  | { status: 'fail'; message: string; rawResult: IWeixinJsBridgeResult | null };

interface IWeixinJsBridge {
  invoke: (
    method: string,
    params: Record<string, string>,
    callback: (result: IWeixinJsBridgeResult) => void,
  ) => void;
}

declare global {
  interface Window {
    WeixinJSBridge?: IWeixinJsBridge;
  }
}

const isWechatBrowser = (): boolean => /micromessenger/i.test(navigator.userAgent);

const cleanAuthorizationParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState({}, '', url.toString());
  debugWechatJsapi('步骤 7/11：已清理 URL 中的 OAuth code 和 state');
};

const createState = (): string => {
  if (window.crypto?.getRandomValues) {
    const values = new Uint32Array(4);
    window.crypto.getRandomValues(values);
    return Array.from(values)
      .map((value) => value.toString(16))
      .join('');
  }

  return `${Date.now()}${Math.random().toString(16).slice(2)}`;
};

const readPendingWechatPay = (): IPendingWechatPay | null => {
  const rawValue = sessionStorage.getItem(PENDING_PAY_KEY);
  if (!rawValue) return null;

  try {
    const pending = JSON.parse(rawValue) as IPendingWechatPay;
    if (
      !pending.orderNo ||
      !pending.appKey ||
      !pending.state ||
      !pending.createdAt ||
      Date.now() - pending.createdAt > AUTHORIZATION_MAX_AGE
    ) {
      sessionStorage.removeItem(PENDING_PAY_KEY);
      debugWechatJsapi('步骤 3/11：待支付授权状态缺失或已过期，已清理');
      return null;
    }
    return pending;
  } catch {
    sessionStorage.removeItem(PENDING_PAY_KEY);
    debugWechatJsapi('步骤 3/11：待支付授权状态解析失败，已清理');
    return null;
  }
};

const savePendingWechatPay = (context: IWechatAuthorizationContext, state: string) => {
  const pending: IPendingWechatPay = {
    ...context,
    state,
    createdAt: Date.now(),
  };
  sessionStorage.setItem(PENDING_PAY_KEY, JSON.stringify(pending));
};

const clearPendingWechatPay = () => sessionStorage.removeItem(PENDING_PAY_KEY);

const matchesPendingContext = (
  pending: IPendingWechatPay | null,
  context: IWechatAuthorizationContext,
  state: string | null,
): pending is IPendingWechatPay => {
  return Boolean(
    pending &&
      state &&
      pending.orderNo === context.orderNo &&
      pending.appKey === context.appKey &&
      pending.state === state,
  );
};

const redirectToWechatAuthorization = (context: IWechatAuthorizationContext, state: string) => {
  const returnUrl = new URL(window.location.href);
  returnUrl.searchParams.delete('code');
  returnUrl.searchParams.delete('state');

  savePendingWechatPay(context, state);
  debugWechatJsapi('步骤 3/11：已保存待支付状态，准备跳转微信 snsapi_base 授权');

  const authorizationUrl = new URL('https://open.weixin.qq.com/connect/oauth2/authorize');
  authorizationUrl.searchParams.set('appid', context.appId || WECHAT_APP_ID);
  authorizationUrl.searchParams.set('redirect_uri', returnUrl.toString());
  authorizationUrl.searchParams.set('response_type', 'code');
  authorizationUrl.searchParams.set('scope', 'snsapi_base');
  authorizationUrl.searchParams.set('state', state);

  window.location.href = `${authorizationUrl.toString()}#wechat_redirect`;
};

/**
 * 有经过校验的 OAuth 回调时，页面加载后可以自动续跑此前的微信支付动作。
 */
const hasPendingWechatAuthorization = (context: IWechatAuthorizationContext): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  const hasCallback = Boolean(searchParams.get('code'));
  const isMatched = matchesPendingContext(
    readPendingWechatPay(),
    context,
    searchParams.get('state'),
  );
  debugWechatJsapi(
    `步骤 4/11：检测 OAuth 回调，hasCode=${hasCallback}，stateAndOrderMatched=${isMatched}`,
  );
  return isMatched && hasCallback;
};

/**
 * 返回当前服务号下的 OpenID。首次调用会跳转微信 OAuth，因此返回 null。
 */
const getWechatOpenId = async (context: IWechatAuthorizationContext): Promise<string | null> => {
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code');
  const state = searchParams.get('state');

  if (code) {
    const pending = readPendingWechatPay();
    if (!matchesPendingContext(pending, context, state)) {
      debugWechatJsapi('步骤 4/11：OAuth state、orderNo 或 appKey 校验失败');
      cleanAuthorizationParams();
      throw new Error('微信授权状态已失效，请重新发起支付');
    }

    try {
      debugWechatJsapi('步骤 5/11：OAuth 回调校验成功，开始用 code 换取 OpenID');
      return await wxLogin(code);
    } catch (error) {
      debugWechatJsapi(`步骤 6/11：OpenID 获取失败，${getWechatDebugError(error)}`);
      throw error;
    } finally {
      clearPendingWechatPay();
      cleanAuthorizationParams();
    }
  }

  debugWechatJsapi('步骤 2/11：未发现 OAuth code，开始微信无感授权');
  redirectToWechatAuthorization(context, createState());
  return null;
};

const waitForWechatBridge = (timeout = 10000): Promise<void> => {
  if (window.WeixinJSBridge) {
    debugWechatJsapi('步骤 8/11：WeixinJSBridge 已就绪');
    return Promise.resolve();
  }

  debugWechatJsapi('步骤 8/11：等待 WeixinJSBridgeReady，最长 10 秒');
  return new Promise((resolve, reject) => {
    const onBridgeReady = () => {
      cleanup();
      debugWechatJsapi('步骤 8/11：已收到 WeixinJSBridgeReady');
      resolve();
    };

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('WeixinJSBridgeReady', onBridgeReady);
    };

    const timeoutId = window.setTimeout(() => {
      cleanup();
      debugWechatJsapi('步骤 8/11：等待 WeixinJSBridgeReady 超时');
      reject(new Error('微信支付环境未准备完成，请稍后重试'));
    }, timeout);

    document.addEventListener('WeixinJSBridgeReady', onBridgeReady, { once: true });
  });
};

const invokeWechatJsapiPay = (payParams: IWechatJsapiPayParams): Promise<WechatJsapiPayResult> => {
  const bridge = window.WeixinJSBridge;
  if (!bridge) {
    debugWechatJsapi('步骤 10/11：未检测到 WeixinJSBridge，无法调起支付');
    return Promise.resolve({
      status: 'fail',
      message: '请在微信客户端中打开',
      rawResult: null,
    });
  }

  debugWechatJsapi(`步骤 10/11：调起 getBrandWCPayRequest，signType=${payParams.signType}`);
  return new Promise((resolve) => {
    // 兜底：微信部分版本关闭支付面板时不触发任何回调，避免按钮一直卡在 loading。
    const callbackTimeout = window.setTimeout(() => {
      debugWechatJsapi('步骤 10/11：微信支付超时未返回回调，按失败兜底');
      resolve({
        status: 'fail',
        message: '微信支付未返回结果，请确认订单支付状态',
        rawResult: null,
      });
    }, JSAPI_PAY_CALLBACK_TIMEOUT);

    bridge.invoke(
      'getBrandWCPayRequest',
      {
        appId: payParams.appId,
        timeStamp: payParams.timeStamp,
        nonceStr: payParams.nonceStr,
        package: payParams.package,
        signType: payParams.signType,
        paySign: payParams.paySign,
      },
      (result) => {
        window.clearTimeout(callbackTimeout);
        const response = result.err_msg || '未知支付结果';
        const signatureVerificationFailed =
          /签名.*(错误|失败|校验)|signature.*(error|fail|invalid|verify)|sign.*(error|fail|invalid|verify)/i.test(
            response,
          );
        debugWechatJsapi(
          `步骤 10/11：微信支付回调结果=${response}，signatureVerificationFailed=${signatureVerificationFailed}`,
        );
        if (response === 'get_brand_wcpay_request:ok') {
          resolve({ status: 'success', rawResult: result });
        } else if (response === 'get_brand_wcpay_request:cancel') {
          resolve({ status: 'cancel', message: '您已取消支付', rawResult: result });
        } else {
          resolve({ status: 'fail', message: response, rawResult: result });
        }
      },
    );
  });
};

export {
  getWechatOpenId,
  hasPendingWechatAuthorization,
  invokeWechatJsapiPay,
  isWechatBrowser,
  waitForWechatBridge,
};
export type { IWechatAuthorizationContext, IWechatJsapiPayParams, WechatJsapiPayResult };
